import React, { useState } from 'react';
import { Play, Video, ExternalLink, Sparkles } from 'lucide-react';

interface ResponsiveYouTubeEmbedProps {
    videoId?: string;
    videoUrl?: string;
    title: string;
    description?: string;
    stepBadge?: string;
}

// Helper to extract a YouTube Video ID from standard formats
export const extractYouTubeId = (urlOrId?: string): string | null => {
    if (!urlOrId) return null;
    const clean = urlOrId.trim();
    // If it's already an 11-character YouTube video ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(clean)) {
        return clean;
    }
    // youtu.be/<id>
    const shortMatch = clean.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (shortMatch) return shortMatch[1];
    // youtube.com/watch?v=<id>
    const standardMatch = clean.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (standardMatch) return standardMatch[1];
    // youtube.com/embed/<id>
    const embedMatch = clean.match(/embed\/([a-zA-Z0-9_-]{11})/);
    if (embedMatch) return embedMatch[1];
    return null;
};

export const ResponsiveYouTubeEmbed: React.FC<ResponsiveYouTubeEmbedProps> = ({
    videoId,
    videoUrl,
    title,
    description,
    stepBadge
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const resolvedId = extractYouTubeId(videoId) || extractYouTubeId(videoUrl);

    return (
        <div className="w-full rounded-2xl md:rounded-3xl overflow-hidden bg-brand-charcoal border border-brand-border/60 shadow-xl group">
            {/* Aspect-ratio locked 16:9 container */}
            <div className="relative w-full aspect-video bg-black flex items-center justify-center overflow-hidden">
                {resolvedId ? (
                    isPlaying ? (
                        <iframe
                            className="absolute inset-0 w-full h-full border-0"
                            src={`https://www.youtube-nocookie.com/embed/${resolvedId}?autoplay=1&rel=0&modestbranding=1`}
                            title={title}
                            loading="lazy"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        />
                    ) : (
                        /* High-performance preview façade with instant play on click */
                        <div 
                            onClick={() => setIsPlaying(true)}
                            className="relative w-full h-full cursor-pointer group/facade"
                            role="button"
                            tabIndex={0}
                            aria-label={`Play ${title}`}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setIsPlaying(true);
                                }
                            }}
                        >
                            {/* High-res YouTube thumbnail */}
                            <img
                                src={`https://img.youtube.com/vi/${resolvedId}/hqdefault.jpg`}
                                alt={title}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover/facade:scale-105 opacity-85"
                                loading="lazy"
                                onError={(e) => {
                                    // Fallback if maxres is not available
                                    (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${resolvedId}/0.jpg`;
                                }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                            {/* Centered Play Button */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-brand-emerald text-white flex items-center justify-center shadow-2xl shadow-brand-emerald/40 transform group-hover/facade:scale-110 transition-all duration-300 ring-4 ring-white/20">
                                    <Play size={28} className="fill-white translate-x-0.5" />
                                </div>
                            </div>

                            {/* Top & Bottom Badges on Facade */}
                            <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                                {stepBadge && (
                                    <span className="px-3 py-1 bg-black/60 backdrop-blur-md text-brand-emerald border border-brand-emerald/30 rounded-full text-[10px] font-black uppercase tracking-widest">
                                        {stepBadge}
                                    </span>
                                )}
                                <span className="px-3 py-1 bg-black/60 backdrop-blur-md text-white/80 rounded-full text-[10px] font-bold">
                                    LGL Video Guide
                                </span>
                            </div>

                            <div className="absolute bottom-4 left-4 right-4 text-left">
                                <h4 className="text-white font-black text-sm sm:text-base md:text-lg line-clamp-1 drop-shadow-md">
                                    {title}
                                </h4>
                                <p className="text-white/70 text-xs font-medium hidden sm:block">
                                    Click to play video guide directly on your dashboard
                                </p>
                            </div>
                        </div>
                    )
                ) : (
                    /* Elegant placeholder when specific video URL is being finalized */
                    <div className="w-full h-full p-8 flex flex-col items-center justify-center text-center bg-gradient-to-br from-brand-charcoal via-slate-900 to-brand-charcoal text-white relative">
                        <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-4 text-brand-emerald">
                            <Video size={32} />
                        </div>
                        <span className="px-3 py-1 bg-brand-emerald/10 text-brand-emerald border border-brand-emerald/20 rounded-full text-[10px] font-black uppercase tracking-widest mb-2">
                            Video Guide Ready to Link
                        </span>
                        <h4 className="text-lg font-black text-white mb-2">{title}</h4>
                        <p className="text-xs text-white/60 max-w-md font-medium leading-relaxed mb-4">
                            The LGL instruction video for this software can be watched directly here once configured in the project.
                        </p>
                        <div className="flex items-center gap-2 text-[11px] text-brand-emerald font-bold">
                            <Sparkles size={14} /> Full step-by-step screen recording guide
                        </div>
                    </div>
                )}
            </div>

            {/* Video Footer Metadata */}
            {description && (
                <div className="p-4 sm:p-5 bg-white dark:bg-brand-charcoal border-t border-brand-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <p className="text-xs text-brand-muted font-medium leading-relaxed">
                        {description}
                    </p>
                    {resolvedId && (
                        <a
                            href={`https://www.youtube.com/watch?v=${resolvedId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-brand-muted hover:text-brand-emerald transition-colors shrink-0"
                            title="Open in new tab if preferred"
                        >
                            Open on YouTube <ExternalLink size={12} />
                        </a>
                    )}
                </div>
            )}
        </div>
    );
};
